import { isServer } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

/**
 * Mark the response 404 for a route whose subject does not exist, WITHOUT giving up the streamed
 * shell (TEC-3823).
 *
 * WHY NOT A `notFound()` THROW FROM A LOADER, WHICH IS THE OBVIOUS FIX. The status of an HTML render
 * is decided before the render starts — `loadServerRoute` stores it on the router as
 * `_serverResult.status`, computed by `applyFailure`: 404 if a match is flagged `_notFound`, 500 if a
 * loader threw, else 200. Reaching that from a loader means AWAITING the existence check, and an
 * HTTP status precedes the first byte while the shell IS the first byte. So the await costs the
 * streamed skeleton on every request, including the overwhelming majority that resolve to a real
 * page. That is too much to pay for the status of a missing one.
 *
 * WHAT MAKES A CHEAPER ANSWER POSSIBLE. `renderRouterToStream` reads the status when it constructs
 * the `Response`, and for a BOT it does that after the whole tree has resolved:
 *
 *     const stream = await ReactDOMServer.renderToReadableStream(children, …)
 *     if (isbot(request.headers.get('User-Agent'))) await waitForReadyOrAbort(stream.allReady, …)
 *     …
 *     new Response(responseStream, {
 *       status: router._serverResult?.type === 'render' ? router._serverResult.status : 200,
 *     })
 *
 * So a value written during the render is still in time for a crawler's response, and the human path
 * — read at shell time — is untouched. Both runtimes we ship take that branch: `react-dom/server`
 * exposes `renderToReadableStream` under Node AND under Bun (Bun has no `renderToPipeableStream` at
 * all), and the readable branch is tested first.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. A human still gets 200. That is the conscious limit: the harm
 * a soft 404 does is search engines indexing pages that do not exist, and a visitor already sees a
 * rendered not-found page. Making it 404 for a human too means blocking the shell on an existence
 * check — `resolveCampaignSlugOrId` is the cheapest primitive if that is ever wanted.
 *
 * SERVER ONLY, and the guard is load-bearing rather than an optimisation: there is nothing to set on
 * the client (a client navigation has no HTTP status), and mutating router internals during a client
 * render is exactly the kind of thing that loops.
 */
export function useNotFoundStatus(missing: boolean): void {
	const router = useRouter();
	if (!isServer || !missing) return;
	markServerResultNotFound(router);
}

/** The one field of the router's server result this reads. See {@link markServerResultNotFound}. */
interface MutableServerRenderResult {
	type: 'render';
	status: number;
}

/**
 * Set the pending server render's status to 404, if the router still looks the way we expect.
 *
 * SEPARATED FROM THE HOOK SO IT CAN BE TESTED, and because it is the half that carries risk:
 * `_serverResult` is PRIVATE. It is not in `RouterStores`, it is not in the public types, and the
 * mechanism has already moved once — `1.170.17` read `router.stores.statusCode` here, `1.170.32`
 * reads `router._serverResult.status`. A minor bump changed it.
 *
 * So this FAILS OPEN by design. If the field is missing, or is a redirect rather than a render, or
 * its status is not a number, nothing happens and the response keeps whatever status it would have
 * had. A soft 404 after an upgrade is a regression worth a ticket; a crash on every portal page
 * because an internal was renamed is an outage. `not-found-status.unit.test.ts` pins the upstream
 * shape so an upgrade fails a test rather than quietly going back to answering 200.
 */
export function markServerResultNotFound(router: unknown): void {
	if (typeof router !== 'object' || router === null) return;
	const candidate = (router as { _serverResult?: unknown })._serverResult;
	if (typeof candidate !== 'object' || candidate === null) return;
	const result = candidate as { type?: unknown; status?: unknown };
	if (result.type !== 'render' || typeof result.status !== 'number') return;
	(result as MutableServerRenderResult).status = 404;
}
